import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  ChatHotelResultCard,
  ChatHotelRoom,
  RuleActionExtension
} from '@sabstravtech/obtservices/base';
import { ModalOpenerService } from '@sabstravtech/obtservices/angular';
import { LightningModalTypes } from '../../../classes/modal-types.enum';
import { formatCardCurrency, policyStatusClass, policyStatusLabel } from '../chat-card.util';
import {
  faCalendarDays,
  faLocationDot,
  faChevronDown,
  faChevronUp
} from '@fortawesome/free-solid-svg-icons';

const ROOMS_PREVIEW_COUNT = 3;

/** ENT-16881 — identifies which room of which hotel card the user picked. */
export interface ChatHotelRoomSelection {
  hotelIndex: number;
  roomId: string;
}

@Component({
  selector: 'app-chat-hotel-card',
  standalone: false,
  templateUrl: './chat-hotel-card.component.html',
  styleUrls: ['./chat-hotel-card.component.scss']
})
export class ChatHotelCardComponent {
  @Input() card!: ChatHotelResultCard;
  /** ENT-16247 — id of the assistant message these results belong to. */
  @Input() feedbackBaseId = '';
  @Output() hotelNameClick = new EventEmitter<string>();
  /** ENT-16881 — emitted when the user clicks a room's rate price, to add
   *  that specific room to the basket and start the booking journey. */
  @Output() roomSelect = new EventEmitter<ChatHotelRoomSelection>();

  /** ENT-16247 — synthetic feedback id, unique per card. */
  get feedbackId(): string {
    return `${this.feedbackBaseId || 'card'}::hotel-${this.card.index}`;
  }

  /** ENT-16247 — short card summary sent as the feedback assistantResponse. */
  get feedbackSummary(): string {
    return `Hotel ${this.card.name}, from ${this.card.pricePerNight} ${this.card.currencyCode || ''}`.trim();
  }

  faLocationDot = faLocationDot;
  faCalendarDays = faCalendarDays;
  faChevronDown = faChevronDown;
  faChevronUp = faChevronUp;

  isExpanded = false;
  showAllRates = false;

  constructor(private modalOpenerService: ModalOpenerService) {}

  get policyClass(): string {
    return policyStatusClass(this.card.policyStatus);
  }

  get policyLabel(): string {
    return policyStatusLabel(this.policyClass);
  }

  /** Headline room's policy fields, shaped for `app-warning-tags` — the same
   *  indicator/tooltip component the results page uses, so the chat card and
   *  results page render identically instead of a bespoke text box. */
  get headlineWarningTicket(): RuleActionExtension {
    return {
      unavailable: !!this.card.unavailable,
      unavailableMessage: this.card.unavailableMessage || '',
      requiresReasonKeys: this.card.requiresReasonKeys || [],
      requiresReasonMessages: this.card.requiresReasonMessages || []
    };
  }

  /** Per-room policy fields for the expanded room list, shaped for
   *  `app-warning-tags` — a room can be out of policy independently of the
   *  headline card's cheapest room. */
  warningTicketFor(room: ChatHotelRoom): RuleActionExtension {
    return {
      unavailable: !!room.unavailable,
      unavailableMessage: room.unavailableMessage || '',
      requiresReasonKeys: room.requiresReasonKeys || [],
      requiresReasonMessages: room.requiresReasonMessages || []
    };
  }

  get formattedPrice(): string {
    return this.formatPrice(this.card.price, this.card.currencyCode ?? 'GBP');
  }

  get displayedRooms(): ChatHotelRoom[] {
    const rooms = this.card.rooms ?? [];
    return this.showAllRates ? rooms : rooms.slice(0, ROOMS_PREVIEW_COUNT);
  }

  get hiddenRoomsCount(): number {
    return (this.card.rooms?.length ?? 0) - ROOMS_PREVIEW_COUNT;
  }

  formatRoomPrice(room: ChatHotelRoom): string {
    return this.formatPrice(room.total, room.currencyCode ?? 'GBP');
  }

  private formatPrice(amount: number, currencyCode: string): string {
    return formatCardCurrency(amount, currencyCode);
  }

  toggleExpand(): void {
    this.isExpanded = !this.isExpanded;
  }

  navigateToHotel(event: Event): void {
    event.stopPropagation();
    this.hotelNameClick.emit(this.card.name);
  }

  openBookingConditions(room: ChatHotelRoom, event: Event): void {
    event.stopPropagation();
    this.modalOpenerService.open(
      LightningModalTypes.GmtHotelModalFareRulesComponent,
      { windowClass: 'fare-rules-modal', centered: true },
      { room, source: this.card.source ?? '', name: this.card.name }
    );
  }

  /** ENT-16881 — clicking a room's rate price selects that room and adds it
   *  to the basket (mirrors the flight/rail card's rate-click pattern). */
  onSelectRoom(room: ChatHotelRoom, event: Event): void {
    event.stopPropagation();
    if (room.unavailable) return;
    this.roomSelect.emit({ hotelIndex: this.card.index, roomId: room.roomId });
  }
}
